/**
 * Generador de scripts de Hotspot
 * Migrado de Python (backend/server.py) a JavaScript
 */

import { 
  getInterfaceSetCommand,
  getBaseIp,
  getNetwork,
  getFormattedDate
} from '../utils/routeros.js';

import { validateHotspotConfig } from '../utils/validators.js';

/**
 * Genera perfiles de usuario para hotspot
 * @param {Array} perfiles - Lista de perfiles con tiempo límite
 * @returns {string} Script de perfiles
 */
function generateHotspotProfiles(perfiles) {
  let script = "/ip hotspot user profile\n";
  
  // Mapeo de tiempos
  const timeMappings = {
    "01 - 15 MINUTOS": "00:15:00",
    "02 - 30 MINUTOS": "00:30:00",
    "03 - 1 HORA": "01:00:00",
    "04 - 2 HORAS": "02:00:00",
    "05 - 3 HORAS": "03:00:00",
    "08 - 6 HORAS": "06:00:00",
    "09 - 12 HORAS": "12:00:00",
    "10 - 1 DIA": "1d 00:00:00",
    "16 - 7 DIAS": "7d 00:00:00",
    "17 - 14 DIAS": "14d 00:00:00",
    "19 - 30 DIAS": "30d 00:00:00"
  };
  
  perfiles.forEach(perfil => {
    const sessionTimeout = timeMappings[perfil.nombre] || "01:00:00";
    script += `add name="${perfil.nombre}" session-timeout=${sessionTimeout} \\\n`;
    script += `    shared-users=1 rate-limit=2M/2M\n`;
  });
  
  return script;
}

/**
 * Genera fichas/vouchers de usuarios
 * @param {Array} perfiles - Lista de perfiles con cantidad
 * @returns {string} Script de usuarios
 */
function generateFichas(perfiles) {
  let script = "/ip hotspot user\n";
  const fecha = new Date().toLocaleString('es-MX', {
    day: '2-digit',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  perfiles.forEach(perfil => {
    for (let i = 0; i < perfil.cantidad; i++) {
      // Generar password aleatorio de 3 dígitos
      const password = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      // Generar username con código + número incremental
      const username = `${perfil.codigo}${i + 1}`;
      
      script += `add comment="Creacion ${fecha}" name=${username} password=${password} profile=\\\n`;
      script += `    "${perfil.nombre}"\n`;
    }
  });
  
  return script;
}

/**
 * Genera script completo de Hotspot
 * @param {object} config - Configuración completa
 * @returns {string} Script generado
 */
export function generateHotspotScript(config) {
  validateHotspotConfig(config);
  
  const lanCidr = config.router.subnetLan;
  const lanNetwork = getNetwork(lanCidr);
  const baseIp = getBaseIp(lanCidr);
  const lanIp = `${baseIp}.1`;
  const dnsServers = config.router.dnsServers.join(',');
  
  let wifiStatus = "";
  if (config.wifi && config.wifi.tieneWifi && config.wifi.activar) {
    wifiStatus = " + WiFi";
  }
  
  let script = `# MikroTik Hotspot Script
# Generated: ${getFormattedDate()}
# Configuration: Hotspot${config.conFichas ? ' con Fichas' : ' sin Fichas'}${wifiStatus}

/system identity
set name="${config.router.nombre}"

/system clock
set time-zone-name=${config.router.zonaHoraria}

/interface bridge
add name=bridge-local

`;
  
  // Configure WAN interface
  script += `/interface ethernet
set [ find default-name=${config.wan.puerto} ] name=WAN

`;
  
  // Configure WiFi if enabled
  if (config.wifi && config.wifi.tieneWifi && config.wifi.activar) {
    script += `/interface wireless
set [ find default-name=wlan1 ] band=2ghz-b/g/n disabled=no mode=ap-bridge \\
    ssid="${config.wifi.ssid}" wds-mode=dynamic

/interface wireless security-profiles
set [ find default=yes ] supplicant-identity=MikroTik

`;
  }
  
  // IP Pool for Hotspot
  const dhcpStart = `${baseIp}.2`;
  const dhcpEnd = `${baseIp}.254`;
  
  script += `/ip pool
add name=hotspot-pool ranges=${dhcpStart}-${dhcpEnd}

/ip address
add address=${lanNetwork}/24 interface=bridge-local network=${lanNetwork}
`;
  
  // WAN IP configuration
  if (config.wan.tipo === "fijo") {
    const wanNetwork = config.wan.ip.split('.').slice(0, 3).join('.') + '.0';
    script += `add address=${config.wan.ip}/24 interface=WAN network=${wanNetwork}\n`;
  }
  
  script += "\n";
  
  // DHCP Client for WAN if needed
  if (config.wan.tipo === "dhcp") {
    script += `/ip dhcp-client
add default-route-distance=0 dhcp-options=hostname,clientid interface=WAN

`;
  }
  
  // Hotspot server profile
  script += `/ip hotspot profile
add dns-name=hotspot.local hotspot-address=${lanIp} name=hsprof1

`;
  
  // Generate user profiles if con fichas
  if (config.conFichas && config.perfiles && config.perfiles.length > 0) {
    script += generateHotspotProfiles(config.perfiles);
    script += "\n";
  }
  
  // Hotspot server
  script += `/ip hotspot
add address-pool=hotspot-pool disabled=no interface=bridge-local name=hotspot1 profile=hsprof1

/ip hotspot service-port
set ftp disabled=yes

`;
  
  // Generate fichas if needed
  if (config.conFichas && config.perfiles && config.perfiles.length > 0) {
    script += generateFichas(config.perfiles);
    script += "\n";
  }
  
  // DNS
  script += `/ip dns
set allow-remote-requests=yes servers=${dnsServers}

`;
  
  // Firewall NAT
  script += `/ip firewall nat
add action=masquerade chain=srcnat out-interface=WAN

`;
  
  // Routes
  if (config.wan.tipo === "fijo") {
    script += `/ip route
add check-gateway=ping distance=1 gateway=${config.wan.gateway}

`;
  }
  
  // ============================================
  // CONFIGURACIONES ADICIONALES DEL SISTEMA
  // ============================================
  
  const sysConfig = config.systemConfig || {};
  
  // Deshabilitar servicio WWW
  if (sysConfig.deshabilitarWww) {
    script += `/ip service
set www disabled=yes

`;
  }
  
  // Configurar NTP
  if (sysConfig.configurarNtp) {
    script += `/system ntp client
set enabled=yes primary-ntp=${sysConfig.ntpServer || '0.pool.ntp.org'}

`;
  }
  
  // Reinicio programado
  if (sysConfig.reinicioProgramado) {
    const hora = sysConfig.horaReinicio || '03:00';
    const horaNum = hora.split(':')[0];
    script += `/system scheduler
add interval=1d name="Reiniciar ${horaNum} AM" \\
    on-event="/system reboot" policy=reboot,read,write,policy,test,password,sniff,sensitive \\
    start-date=jan/01/1970 start-time=${hora}

`;
  }
  
  // Crear usuario administrador personalizado
  if (sysConfig.crearUsuarioAdmin) {
    const adminUser = config.adminUsername || 'admin-custom';
    const adminPass = config.adminPassword || 'admin123';
    script += `/user
add disable=no group=full name=${adminUser} password=${adminPass}

`;
  }
  
  // Eliminar usuario admin por defecto
  if (sysConfig.eliminarAdminDefault) {
    script += `/user
remove [find name=admin]

`;
  }
  
  // Bloquear compartir por Bluetooth
  if (sysConfig.bloquearBluetooth) {
    script += `/ip firewall mangle
add action=change-ttl chain=postrouting comment="BLOQUEO DE BLUETOOTH" \\
    dst-address=${lanNetwork}/24 new-ttl=set:1

`;
  }
  
  // Script de limpieza automática
  if (sysConfig.limpiezaAutomatica) {
    const intervalo = sysConfig.intervaloLimpieza || '1d';
    script += `/system scheduler
add name="Limpiar_Usuarios_Expirados" interval=${intervalo} \\
    on-event="{\\r\\
\\n:local currentTime [/system clock get time]\\r\\
\\n:local currentDate [/system clock get date]\\r\\
\\n:foreach user in=[/ip hotspot user find] do={\\r\\
\\n  :local userName [/ip hotspot user get \\$user name]\\r\\
\\n  :local userDisabled [/ip hotspot user get \\$user disabled]\\r\\
\\n  \\r\\
\\n  # Verificar si el usuario está deshabilitado\\r\\
\\n  :if (\\$userDisabled) do={\\r\\
\\n    # Buscar scheduler asociado\\r\\
\\n    :local schedExists [/system scheduler find name=\\$userName]\\r\\
\\n    :if ([:len \\$schedExists] = 0) do={\\r\\
\\n      # No hay scheduler, el usuario ya expiró\\r\\
\\n      /ip hotspot user remove \\$user\\r\\
\\n      /log info \\"Usuario eliminado: \\$userName (expirado sin scheduler)\\"\\r\\
\\n    }\\r\\
\\n  }\\r\\
\\n}\\r\\
\\n\\r\\
\\n# Limpiar schedulers huérfanos\\r\\
\\n:foreach sched in=[/system scheduler find] do={\\r\\
\\n  :local schedName [/system scheduler get \\$sched name]\\r\\
\\n  :local userExists [/ip hotspot user find name=\\$schedName]\\r\\
\\n  :if ([:len \\$userExists] = 0) do={\\r\\
\\n    /system scheduler remove \\$sched\\r\\
\\n    /log info \\"Scheduler huérfano eliminado: \\$schedName\\"\\r\\
\\n  }\\r\\
\\n}\\r\\
\\n}" start-time=startup policy=read,write,policy,test

`;
  }
  
  // Habilitar lector QR (walled-garden)
  if (sysConfig.habilitarQr) {
    script += `/ip hotspot walled-garden ip
add dst-address=laksa19.github.io comment="Lector QR"

`;
  }
  
  // Nota personalizada del sistema
  if (sysConfig.notaPersonalizada && sysConfig.textoNota) {
    // Escapar caracteres especiales
    const notaEscapada = sysConfig.textoNota
      .replace(/"/g, '\\"')
      .replace(/\n/g, '\\r\\n');
    script += `/system note
set note="${notaEscapada}"

`;
  }
  
  script += `/system routerboard settings
set protected-routerboot=disabled
`;
  
  // ============================================
  // BRIDGE PORT AL FINAL (para evitar cortes)
  // ============================================
  script += "\n/interface bridge port\n";
  const puertosLan = config.puertosLan || 4;
  for (let i = 1; i <= puertosLan; i++) {
    // Asumiendo WAN es ether1, LAN empieza desde ether2
    const lanPort = `ether${i + 1}`;
    script += `add bridge=bridge-local interface=${lanPort}\n`;
  }
  
  // Add wlan1 to bridge if WiFi is enabled
  if (config.wifi && config.wifi.tieneWifi && config.wifi.activar) {
    script += "add bridge=bridge-local interface=wlan1\n";
  }
  
  return script;
}
